import api from './api'
import i18n from '../i18n'

export interface PaymentOnline {
  uuid: string
  payment_uuid: string
  club_uuid: string
  payment_method_uuid: string
  terms_document_uuid: string
  privacy_document_uuid: string
  provider: string
  transaction_id: string | null
  payment_hash: string
  amount: number
  currency: string
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'REFUNDED'
  payer_email: string
  payer_name: string
  payer_ip: string | null
  metadata: Record<string, any> | null
  paid_at: string | null
  created_at: string
  updated_at: string
}

export interface InitiatePaymentDto {
  payer_email: string
  payer_name: string
  return_url: string
  cancel_url: string
  other_document_uuids?: string[]
}

export interface InitiatePaymentResponse {
  message: string
  payment_online_uuid: string
  payment_url: string
  transaction_id: string | null
  status: string
}

/**
 * Payment Online Service
 * Handles all online payment-related API calls
 */
class PaymentOnlineService {
  /**
   * Initiate online payment
   * POST v1/{club}/payments/{payment}/initiate
   */
  async initiatePayment(
    clubUuid: string,
    paymentUuid: string,
    data: InitiatePaymentDto
  ): Promise<InitiatePaymentResponse> {
    const response = await api.post(`v1/${clubUuid}/payments/${paymentUuid}/initiate`, data)
    return response.data
  }

  /**
   * Get payment online status
   * GET v1/{club}/payments-online/{paymentOnline}
   */
  async getPaymentOnlineStatus(clubUuid: string, paymentOnlineUuid: string): Promise<{ payment_online: PaymentOnline }> {
    const response = await api.get(`v1/${clubUuid}/payments-online/${paymentOnlineUuid}`)
    return response.data
  }

  /**
   * Get status display name
   */
  getStatusName(status: string): string {
    const t = i18n.global.t as (key: string) => string
    const key = `paymentOnlineStatus.${status}`
    const translated = t(key)
    return translated !== key ? translated : status
  }

  /**
   * Get status color class
   */
  getStatusColorClass(status: string): string {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      REFUNDED: 'bg-purple-100 text-purple-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  /**
   * Generate return URL for payment success
   */
  generateReturnUrl(paymentUuid: string, paymentHash: string): string {
    const baseUrl = window.location.origin
    return `${baseUrl}/payment/${paymentUuid}/${paymentHash}?status=processing`
  }

  /**
   * Generate cancel URL for payment cancellation
   */
  generateCancelUrl(paymentUuid: string, paymentHash: string): string {
    const baseUrl = window.location.origin
    return `${baseUrl}/payment/${paymentUuid}/${paymentHash}?status=cancelled`
  }
}

export default new PaymentOnlineService()
